import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDateTime, statusColor } from "@/lib/utils-format";
import { Plus, Phone, MessageSquare, Mail, Users as UsersIcon, CalendarClock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["call", "sms", "whatsapp", "email", "meeting"];
const ICON = { call: Phone, sms: MessageSquare, whatsapp: MessageSquare, email: Mail, meeting: UsersIcon };

const empty = { lead_id: "", customer_id: "", type: "call", scheduled_at: "", notes: "", assigned_to: "", status: "pending" };

export default function FollowUpsPage() {
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [f, l, c, u] = await Promise.all([
      api.get("/followups"), api.get("/leads"), api.get("/customers"), api.get("/users"),
    ]);
    setItems(f.data); setLeads(l.data); setCustomers(c.data); setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.scheduled_at) return toast.error("Set a date/time");
    try {
      const payload = { ...form, lead_id: form.lead_id || null, customer_id: form.customer_id || null, assigned_to: form.assigned_to || null };
      await api.post("/followups", payload);
      toast.success("Follow-up scheduled");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const setStatus = async (f, status) => {
    await api.patch(`/followups/${f.id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const filtered = items.filter((i) => filter === "all" || i.status === filter);

  return (
    <div className="space-y-6" data-testid="followups-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Reminders</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Follow-ups</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="followup-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="missed">Missed</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="followup-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Schedule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white">
              <DialogHeader><DialogTitle className="font-[Outfit]">New Follow-up</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="followup-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Scheduled at *</Label>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="followup-datetime-input" />
                </div>
                <div>
                  <Label>Related Lead</Label>
                  <Select value={form.lead_id || "none"} onValueChange={(v) => setForm({ ...form, lead_id: v === "none" ? "" : v, customer_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      <SelectItem value="none">None</SelectItem>
                      {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Related Customer</Label>
                  <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v, lead_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      <SelectItem value="none">None</SelectItem>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned to</Label>
                  <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} data-testid="followup-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((f) => {
          const Icon = ICON[f.type] || CalendarClock;
          const lead = leads.find((l) => l.id === f.lead_id);
          const cust = customers.find((c) => c.id === f.customer_id);
          const usr = users.find((u) => u.id === f.assigned_to);
          return (
            <div key={f.id} data-testid={`followup-row-${f.id}`} className="bg-white border border-[#EAE5D9] rounded-md p-4 flex items-start gap-4 hover-lift">
              <div className="w-10 h-10 rounded-md bg-[#FEEBC8] text-[#C05621] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold capitalize">{f.type}</span>
                  <Badge variant="outline" className={`capitalize ${statusColor(f.status)}`}>{f.status}</Badge>
                  {lead && <span className="text-xs text-[#8B7355]">→ Lead: {lead.name}</span>}
                  {cust && <span className="text-xs text-[#8B7355]">→ Customer: {cust.name}</span>}
                  {usr && <span className="text-xs text-[#8B7355]">· {usr.name}</span>}
                </div>
                <div className="text-xs text-[#5C4033] mt-0.5">{fmtDateTime(f.scheduled_at)}</div>
                {f.notes && <p className="text-sm text-[#2C1810] mt-1.5">{f.notes}</p>}
              </div>
              {f.status === "pending" && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setStatus(f, "completed")} data-testid={`followup-complete-${f.id}`} className="p-1.5 hover:bg-emerald-50 text-emerald-700 rounded"><CheckCircle2 className="w-4 h-4" /></button>
                  <button onClick={() => setStatus(f, "missed")} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><XCircle className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#8B7355] bg-white border border-[#EAE5D9] rounded-lg">No follow-ups scheduled.</div>
        )}
      </div>
    </div>
  );
}
